import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from './ConfirmModal';

interface FloatingDeleteButtonWithConfirmationProps {
  showConfirmation?: true;
  modalTitle: string;
  modalMessage: string;
}

interface FloatingDeleteButtonWithoutConfirmationProps {
  showConfirmation: false;
  modalTitle?: string;
  modalMessage?: string;
}

export interface FloatingDeleteButtonBaseProps {
  onDelete: () => Promise<void> | void;
  isDeleting?: boolean;
  disabled?: boolean;
  labelKey?: string;
}

export type FloatingDeleteButtonProps =
  | (FloatingDeleteButtonBaseProps & FloatingDeleteButtonWithConfirmationProps)
  | (FloatingDeleteButtonBaseProps & FloatingDeleteButtonWithoutConfirmationProps);

/**
 * Reusable delete action for floating bars that can execute directly or ask for confirmation first.
 */
export function FloatingDeleteButton({
  onDelete,
  isDeleting = false,
  disabled = false,
  labelKey = 'common.delete',
  modalTitle,
  modalMessage,
  showConfirmation = true,
}: FloatingDeleteButtonProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isDisabled = isDeleting || disabled;

  const openConfirmationModal = () => {
    setIsModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsModalOpen(false);
  };

  const handleClick = async () => {
    if (showConfirmation) {
      openConfirmationModal();
      return;
    }

    await onDelete();
  };

  const handleConfirm = async () => {
    await onDelete();
    closeConfirmationModal();
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-950/30"
      >
        {isDeleting ? (
          <div className="size-4 animate-spin rounded-full border-2 border-t-transparent border-red-600" />
        ) : (
          <Trash2 className="size-4" aria-hidden="true" />
        )}
        {t(labelKey)}
      </button>

      {showConfirmation ? (
        <ConfirmModal
          isOpen={isModalOpen}
          title={modalTitle ?? ''}
          message={modalMessage ?? ''}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          onConfirm={handleConfirm}
          onCancel={closeConfirmationModal}
          isLoading={isDeleting}
          variant="danger"
        />
      ) : null}
    </>
  );
}
